import * as React from 'react';

import { observer } from 'mobx-react';
import styled from 'styled-components';

import { ContentSpacing } from '@estee/elc-base-theme';
import { IOrderConfig, OrderRender } from '@estee/elc-layouts';
import { Overlay } from '@estee/elc-overlay';
import { config } from '@estee/elc-service';
import { ArrayMinOne } from '@estee/elc-universal-utils';

import { getSidebarViewRegistry, ISidebarFeature } from '~views/sidebar/registry';
import { serviceName } from '~setup/packageInfo';
import { classes } from '~utils/Classes';

import AccountSidebarController from './AccountSidebarController';
import { SidebarControls } from './SidebarControls';

export interface IAccountSideBar {
    viewController: AccountSidebarController;
    config: {
        accountSidebarConfig?: IOrderConfig;
    };
}

const AccountSidebarWrapper = styled.div`
    display: grid;
    grid-template-rows: min-content;
    height: 100%;
    padding: ${ContentSpacing.space10} 0;
    overflow-y: auto;
`;

export const TEST_ID_ACCOUNT_SIDEBAR = 'account-sidebar';
export const TEST_ID_ACCOUNT_SIDEBAR_ORDERABLE = 'account-sidebar-orderable-content';

export const accountConfigs: ArrayMinOne<string> = ['accountSidebarConfig'];
const accountSidebarClass = classes('account-sidebar');

@config(accountConfigs)
@observer
class AccountSidebar extends React.Component<IAccountSideBar> {
    public static displayName: string;
    private accountSidebarRef: React.RefObject<HTMLDivElement> = React.createRef();
    private closeBtnRef: React.RefObject<HTMLButtonElement> = React.createRef();

    constructor(props: IAccountSideBar) {
        super(props);
        props.viewController.setOrderableConfig(props.config.accountSidebarConfig || {});
    }

    public componentDidMount = async () => {
        await this.props.viewController.loaded();
    };

    onOverlayTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
        const overlay = e.target as HTMLDivElement;
        e.stopPropagation();

        if (overlay.classList.contains('elc-overlay-sidebar')) {
            this.closeBtnRef?.current?.focus();
        }
    };
    public render() {
        const {
            hideOverlay,
            accountSidebarConfig = {},
            data: { isOverlayVisible }
        } = this.props.viewController;

        const featureConfig: ISidebarFeature = {
            componentConfig: accountSidebarConfig,
            controller: this.props.viewController
        };

        return (
            <Overlay
                hideOverlay={hideOverlay}
                isOverlayVisible={isOverlayVisible}
                data-test-id={TEST_ID_ACCOUNT_SIDEBAR}
                handleTransitionEnd={this.onOverlayTransitionEnd}
            >
                <SidebarControls hideOverlay={hideOverlay} closeBtnRef={this.closeBtnRef} />
                {isOverlayVisible && (
                    <AccountSidebarWrapper
                        ref={this.accountSidebarRef}
                        className={accountSidebarClass}
                    >
                        <OrderRender
                            data-test-id={TEST_ID_ACCOUNT_SIDEBAR_ORDERABLE}
                            orderConfig={accountSidebarConfig}
                            serviceName={serviceName}
                            viewRegistry={getSidebarViewRegistry(featureConfig)}
                        />
                    </AccountSidebarWrapper>
                )}
            </Overlay>
        );
    }
}

AccountSidebar.displayName = 'AccountSidebar';

export default AccountSidebar;
